'use client';

import { useState, useEffect, useRef } from 'react';
import { LANDING_IMAGES } from '@/lib/images';
import { FallbackImage } from '@/components/ui/FallbackImage';
import { uiFb } from '@/lib/i18n';
import { useScrollReveal } from './useScrollReveal';

interface LandingSocialProofProps {
  eventCount: number;
  lang?: string;
}

const STAT_DELAYS = ['delay-200', 'delay-300', 'delay-400', 'delay-500', 'delay-600'];

// #5 — Count-up animation hook
function useCountUp(target: number, duration: number, start: boolean): number {
  const [value, setValue] = useState(0);
  const frameRef = useRef<number>(0);

  useEffect(() => {
    if (!start) return;
    if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setValue(target);
      return;
    }
    const startTime = performance.now();
    const animate = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(eased * target));
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(animate);
      }
    };
    frameRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frameRef.current);
  }, [target, duration, start]);

  return value;
}

export function LandingSocialProof({ eventCount, lang = 'en' }: LandingSocialProofProps) {
  const { ref, revealed } = useScrollReveal();

  const countCountries = useCountUp(14, 1200, revealed);
  const countEvents = useCountUp(eventCount, 1800, revealed);
  const countLenses = useCountUp(5, 800, revealed);
  const countLanguages = useCountUp(6, 1000, revealed);

  const stats = [
    { value: `${countCountries}`, label: uiFb('ui.sp_countries', 'Countries', lang), sub: uiFb('ui.sp_countries_sub', 'Asia, Middle East, Africa', lang) },
    { value: `${countEvents}+`, label: uiFb('ui.sp_holidays', 'Holidays & Festivals', lang), sub: uiFb('ui.sp_holidays_sub', 'Always up to date', lang) },
    { value: `${countLenses}`, label: uiFb('ui.sp_explore', 'Ways to Explore', lang), sub: uiFb('ui.sp_explore_sub', 'Country, religion, nature, academic, long weekends', lang) },
    { value: `${countLanguages}`, label: uiFb('ui.sp_languages', 'Languages', lang), sub: uiFb('ui.sp_languages_sub', 'Multilingual pages', lang) },
    { value: uiFb('ui.sp_official', 'Official', lang), label: uiFb('ui.sp_gov_data', 'Government Data', lang), sub: uiFb('ui.sp_gov_data_sub', 'Verified annually', lang) },
  ];

  return (
    <section ref={ref} className="px-4 py-8 md:px-8 md:py-12 lg:px-16 xl:px-20">
      <div className="mx-auto max-w-5xl">
        {/* Coverage map */}
        <FallbackImage
          src={LANDING_IMAGES.coverageMap}
          alt={uiFb('ui.sp_map_alt', 'Map highlighting 14 covered countries across Asia, Middle East and Africa', lang)}
          width={1200}
          height={400}
          sizes="(max-width: 768px) 100vw, 896px"
          className={`scroll-reveal ${revealed ? 'revealed' : ''} mx-auto mb-8 max-h-64 w-full max-w-4xl rounded-xl border border-border-subtle object-cover shadow-sm`}
        />

        <h2
          className={`scroll-reveal ${revealed ? 'revealed' : ''} text-center font-heading text-2xl font-bold tracking-tight text-text-primary md:text-4xl`}
        >
          {uiFb('ui.sp_heading', "What's inside vacant.day?", lang)}
        </h2>

        <div className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-5 md:gap-5">
          {stats.map((stat, i) => (
            <div
              key={stat.label}
              className={`scroll-reveal ${revealed ? 'revealed' : ''} ${STAT_DELAYS[i]} flex flex-col items-center justify-center rounded-xl border border-border-subtle bg-surface-card p-5 text-center md:p-6`}
            >
              <p className="font-heading text-4xl font-black tabular-nums text-brand">{stat.value}</p>
              <p className="mt-1 text-sm font-semibold text-text-primary">{stat.label}</p>
              <p className="mt-0.5 text-label text-muted">{stat.sub}</p>
            </div>
          ))}
        </div>

        <p
          className={`scroll-reveal ${revealed ? 'revealed' : ''} delay-700 mt-8 text-center text-sm font-semibold text-brand`}
        >
          {uiFb('ui.sp_free', 'Always free. No account needed.', lang)}
        </p>
      </div>
    </section>
  );
}
